import { useLayoutEffect } from "react";
import { useNavigate } from "react-router";
import { clearAuth, selectAuth, useAppDispatch, useAppSelector } from "../store";
import { redirectToSsoLogin } from "../SSO/ssoConfig";

/**
 * Clears auth when the access token reaches its expiry (JWT `exp`, typically 60 minutes).
 * Uses `useLayoutEffect` so an already-expired persisted session is cleared before paint.
 *
 * Does not call POST /logout — token expiry only clears client-side state; the server session
 * may remain open until the user signs out explicitly or retention closes it.
 */
export function AuthSessionWatcher() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { accessToken, tokenExpiresAtMs } = useAppSelector(selectAuth);

  useLayoutEffect(() => {
    if (!accessToken || tokenExpiresAtMs == null) {
      return undefined;
    }

    const signOut = () => {
      dispatch(clearAuth());
      if (!redirectToSsoLogin()) {
        navigate("/", { replace: true });
      }
    };

    const msLeft = tokenExpiresAtMs - Date.now();
    if (msLeft <= 0) {
      signOut();
      return undefined;
    }

    const id = window.setTimeout(signOut, msLeft);
    return () => window.clearTimeout(id);
  }, [accessToken, tokenExpiresAtMs, dispatch, navigate]);

  return null;
}
